import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Button,
  Divider,
  Grid,
  Chip,
  IconButton,
  TextField,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Map as MapIcon,
  LocalShipping as DispatchIcon,
  CheckCircle as CompleteIcon,
  Send as SendIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  NavigateNext as NextIcon,
} from "@mui/icons-material";
import { WorkOrderTypeBadge, WorkOrderStatusBadge, WorkOrderPriorityBadge, SLABadge } from "../components";
import { getWorkOrderById, getTechnicians } from "../data/workOrdersData";
import { format, formatDistanceToNow, differenceInMinutes } from "date-fns";

const getStatusProgress = (status: string): number => {
  const progressMap: Record<string, number> = {
    open: 0,
    dispatched: 20,
    tech_aware: 40,
    on_site: 60,
    completed: 80,
    closed: 100,
  };
  return progressMap[status] || 0;
};

export const WorkOrderDetail = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const workOrder = getWorkOrderById(workOrderId || "");
  const technicians = getTechnicians();

  if (!workOrder) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h5" color="text.secondary">
          Work order not found
        </Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/work-orders")} sx={{ mt: 2 }}>
          Back to Work Orders
        </Button>
      </Box>
    );
  }

  const slaDeadline = new Date(workOrder.slaDeadline);
  const now = new Date();
  const minutesRemaining = differenceInMinutes(slaDeadline, now);

  return (
    <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header with actions */}
      <Card variant="outlined" sx={{ mb: 2, flexShrink: 0 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
            <IconButton onClick={() => navigate("/work-orders")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontFamily: "monospace" }}>{workOrder.id}</Typography>
            <WorkOrderPriorityBadge priority={workOrder.priority} />
            <WorkOrderStatusBadge status={workOrder.status} />
            <WorkOrderTypeBadge type={workOrder.type} />
            <Chip label={workOrder.zone} size="small" variant="outlined" />
            {workOrder.slaBreached && <SLABadge breached={true} />}
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(new Date(workOrder.createdAt), { addSuffix: true })}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" startIcon={<MapIcon />} onClick={() => navigate("/geospatial")}>
                Map
              </Button>
              {workOrder.assignedTechnician && (
                <>
                  <Button size="small" variant="outlined" startIcon={<PhoneIcon />}>
                    Call
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<EmailIcon />}>
                    Email
                  </Button>
                </>
              )}
              {workOrder.status === "open" && (
                <Button size="small" variant="contained" startIcon={<DispatchIcon />}>
                  Dispatch
                </Button>
              )}
              {workOrder.status === "on_site" && (
                <Button size="small" variant="contained" color="success" startIcon={<CompleteIcon />}>
                  Complete
                </Button>
              )}
              {workOrder.status === "completed" && (
                <Button size="small" variant="contained" startIcon={<CompleteIcon />}>
                  Close
                </Button>
              )}
              {!["completed", "closed"].includes(workOrder.status) && (
                <Button size="small" variant="outlined" color="error" startIcon={<WarningIcon />}>
                  Escalate
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Progress bar */}
      <Card variant="outlined" sx={{ mb: 2, flexShrink: 0 }}>
        <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>Progress</Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={getStatusProgress(workOrder.status)}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center">
              {["Open", "Dispatched", "Tech Aware", "On Site", "Completed", "Closed"].map((label, i) => (
                <Chip
                  key={label}
                  label={label}
                  size="small"
                  variant={getStatusProgress(workOrder.status) >= (i * 20) ? "filled" : "outlined"}
                  color={getStatusProgress(workOrder.status) >= (i * 20) ? "primary" : "default"}
                  sx={{ fontSize: 10 }}
                />
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Main content */}
      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 8 }} sx={{ height: "100%" }}>
          <Box sx={{ height: "100%", overflow: "auto", pr: 1 }}>
            <Stack spacing={2}>
              {/* Work Order Details Card */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Reference</Typography>
                  <Typography variant="h6" sx={{ fontFamily: "monospace", mb: 2 }}>{workOrder.referenceNumber}</Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {workOrder.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationIcon fontSize="small" color="action" />
                        <Typography variant="body2">{workOrder.location}</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Map Reference</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {workOrder.mapReference}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Coordinates</Typography>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                        {workOrder.coordinates[1].toFixed(4)}, {workOrder.coordinates[0].toFixed(4)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Zone</Typography>
                      <Chip label={workOrder.zone} size="small" variant="outlined" />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <ScheduleIcon fontSize="small" color="action" />
                        <Typography variant="body2">{format(new Date(workOrder.createdAt), "PP p")}</Typography>
                      </Stack>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                      <Typography variant="caption" color={workOrder.slaBreached ? "error" : "text.secondary"}>
                        SLA Deadline
                      </Typography>
                      <Typography variant="body2" color={workOrder.slaBreached ? "error.main" : "text.primary"}>
                        {format(slaDeadline, "PP p")}
                        {!workOrder.slaBreached && minutesRemaining > 0 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({minutesRemaining}m left)
                          </Typography>
                        )}
                      </Typography>
                    </Grid>
                    {workOrder.alertId && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Linked Alert</Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {workOrder.alertId}
                        </Typography>
                      </Grid>
                    )}
                    {workOrder.sightingId && (
                      <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <Typography variant="caption" color="text.secondary">Linked Sighting</Typography>
                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                          {workOrder.sightingId}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>

                  {workOrder.resolution && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="caption" color="text.secondary">Resolution</Typography>
                      <Typography variant="body2">{workOrder.resolution}</Typography>
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Assignment Card */}
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle2" sx={{ minWidth: 80 }}>Assignment</Typography>
                    {workOrder.assignedTechnician ? (
                      <>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main" }}>
                            {workOrder.assignedTechnician.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{workOrder.assignedTechnician.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{workOrder.assignedTechnician.phone}</Typography>
                          </Box>
                        </Stack>
                        <Chip
                          label={workOrder.assignedTechnician.status}
                          size="small"
                          color={workOrder.assignedTechnician.status === "available" ? "success" : workOrder.assignedTechnician.status === "busy" ? "warning" : "default"}
                          variant="outlined"
                        />
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Unassigned</Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" variant="outlined">Reassign</Button>
                  </Stack>
                </CardContent>
              </Card>

              {/* Activity Timeline */}
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>Activity Timeline</Typography>
                  {workOrder.actions && workOrder.actions.length > 0 ? (
                    <Timeline sx={{ p: 0, m: 0 }}>
                      {workOrder.actions.map((action, index) => (
                        <TimelineItem key={action.id} sx={{ "&::before": { display: "none" }, minHeight: 48 }}>
                          <TimelineSeparator>
                            <TimelineDot
                              sx={{ m: 0 }}
                              color={
                                action.type === "close" || action.type === "complete" ? "success" :
                                action.type === "escalate" ? "error" :
                                action.type === "dispatch" ? "warning" :
                                action.type === "on_site" ? "info" : "grey"
                              }
                              variant={index === 0 ? "filled" : "outlined"}
                            />
                            {index < workOrder.actions!.length - 1 && <TimelineConnector />}
                          </TimelineSeparator>
                          <TimelineContent sx={{ py: 0.5, px: 1.5 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box>
                                <Typography variant="body2">{action.description}</Typography>
                                <Typography variant="caption" color="text.secondary">by {action.actor}</Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {formatDistanceToNow(new Date(action.timestamp), { addSuffix: true })}
                              </Typography>
                            </Stack>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    </Timeline>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No activity recorded yet</Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Box>
        </Grid>

        {/* Right Column - Notes */}
        <Grid size={{ xs: 12, lg: 4 }} sx={{ height: "100%" }}>
          <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
              <Typography variant="subtitle2">Notes & Communication</Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
              {workOrder.notes && workOrder.notes.length > 0 ? (
                <Stack spacing={2}>
                  {workOrder.notes.map((note) => (
                    <Box key={note.id} sx={{ display: "flex", gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                        {note.author.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={500}>{note.author}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDistanceToNow(new Date(note.timestamp), { addSuffix: true })}
                          </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {note.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">No notes yet</Typography>
              )}
            </Box>

            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider", flexShrink: 0 }}>
              <Stack direction="row" spacing={1}>
                <TextField placeholder="Add a note..." size="small" fullWidth multiline maxRows={3} />
                <Button variant="contained" size="small" sx={{ minWidth: "auto", px: 1.5 }}>
                  <SendIcon fontSize="small" />
                </Button>
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
